/**
 * Sales-channel setup wizard state (TV1-1904, rule-based rework).
 *
 * The wizard configures a sales channel by CREATING channel-scoped automation
 * rules (not by writing settings onto the integration):
 *   - inventory → a fixed-warehouse stock rule (`builtIn-fixed-warehouse` on
 *     OrderPlaced, or `builtIn-fixed-warehouse-on-status` on OrderStatusChanged),
 *     PLUS a paired channel-stock rule (`builtIn-channel-stock-formula-update`)
 *     that republishes the same fixed warehouse's stock to the channel listing on
 *     every warehouse-stock change. The pair completes the order → warehouse
 *     decrement → channel sync path: the warehouse rule decrements stock, the
 *     channel-stock rule pushes the new value to the channel. It is created
 *     automatically (no separate step); its `formula` is derived from the chosen
 *     warehouse and it is warehouse-stock-triggered, not order-triggered.
 *   - logistics → a fixed-logistics-provider shipment rule (the OrderPlaced or
 *     OrderStatusChanged sibling).
 * Both persist via the `SetRule` command, scoped to the channel
 * (`hierarchyLevel: 'channel'`, `hierarchyEntityIds: [channelId]`). The selected
 * warehouse / logistics provider (+ desired status) become `declaredInputsValues`.
 *
 * Billing is NOT a rule (still being designed): the billing entity is associated
 * with the channel via `UpdateSystemIntegrationConfiguration` (`billingEntityId`),
 * with inline creation support unchanged.
 *
 * Re-run pre-fills each step from the channel's existing rules (the rule's
 * `ruleId` is carried so `SetRule` updates in place rather than duplicating).
 */

import { BillingEntityFormData, emptyBillingEntityFormData } from '@domains/account/state/state';

export interface WarehouseOption {
  id: string;
  label: string;
}

export interface BillingEntityOption {
  id: string;
  name: string;
}

export interface LogisticsIntegrationOption {
  id: string;
  label: string;
  /** Needed to build the rule's `logisticsProvider` declared-input value. */
  connectorId: string;
  integrationUserId: string;
}

export interface InvoicingIntegrationOption {
  id: string;
  label: string;
}

export interface OrderStatusOption {
  id: string;
  name: string;
}

/** Billing / logistics step interaction mode. */
export type StepMode = 'select' | 'create';

/** Which event triggers a step's rule. */
export type TriggerEvent = 'OrderPlaced' | 'OrderStatusChanged';

export interface SalesChannelSetupState {
  /** The channel being configured, synced from the channel readmodel (source: api). */
  data: {
    integrationId: string | null;
    accountId: string | null;
    connectorId: string | null;
    integrationUserId: string | null;
    /** Per-channel completion marker (read from channel.settings). */
    channelSetupCompletedAt: string | null;
    /** Snapshot of channel.settings — the merge base for the completion marker write. */
    currentSettings: Record<string, unknown>;
    /** Current billing entity on the channel (so the billing step pre-selects it). */
    currentBillingEntityId: string | null;
  };

  /** Wizard lifecycle UI state. */
  ui: {
    /** Re-run on demand: the wizard is shown even when setup is already complete. */
    manuallyOpened: boolean;
  };

  /**
   * Publications step-let (TV1-2354) — pull this channel's existing listings in
   * once, right after setup finishes.
   *
   * Deliberately NOT persisted on the integration. It is a one-shot instruction
   * for THIS run of the wizard: checked means "fire one import batch process on
   * complete". There is no recurring auto-import behind it and no domain field
   * backing it, so nothing here needs to survive the modal closing.
   */
  publications: {
    autoImport: boolean;
    /**
     * Historical order import (TV1-2379) — the channel's FULL past order
     * history, in one seeding batch process on complete.
     *
     * Shares this slice with `autoImport` because it is the same class of thing
     * (a one-shot instruction for THIS run of the wizard, nothing persisted),
     * but it is independent of it: either, neither or both can be armed.
     *
     * Defaults ON, like `autoImport` — see `initialState` for why the earlier
     * opt-in default was reversed. Only offered for connectors that satisfy
     * `canImportOrderHistory`.
     */
    autoImportOrders: boolean;
  };

  /** Inventory step — the fixed warehouse that fulfills this channel + its trigger. */
  inventory: {
    warehouseId: string | null;
    trigger: TriggerEvent;
    /** Required when trigger === 'OrderStatusChanged'. */
    statusId: string | null;
    /** Existing warehouse-decrement rule id (re-run) so SetRule updates in place. */
    ruleId: string | null;
    /** Existing channel-stock-formula rule id (re-run) so SetRule updates in place. */
    channelStockRuleId: string | null;
  };

  /** Billing step — select/create an entity + when invoices are generated. */
  billing: {
    mode: StepMode;
    billingEntityId: string | null;
    form: BillingEntityFormData;
    formErrors: Record<string, string>;
    /** Invoice-generation rule trigger. */
    trigger: TriggerEvent;
    /** Order status that generates the invoice (required by the invoice rule). */
    statusId: string | null;
    /** Existing invoice-rule id (re-run) so SetRule updates in place. */
    ruleId: string | null;
  };

  /** Invoicing provisioning — prerequisite for creating a billing entity inline. */
  invoicing: {
    selectedConnectorId: string | null;
    credentials: Record<string, string>;
    provisionedIntegrationId: string | null;
  };

  /** Logistics step — select/register an integration + its trigger. */
  logistics: {
    mode: StepMode;
    logisticsIntegrationId: string | null;
    trigger: TriggerEvent;
    statusId: string | null;
    ruleId: string | null;
    /** Register mode — connector picker + credentials (mirrors AddLogisticsModal). */
    create: {
      selectedConnectorId: string | null;
      credentials: Record<string, string>;
      instanceName: string;
    };
  };

  /** External catalogs (source: api). */
  catalogs: {
    availableWarehouses: WarehouseOption[];
    billingEntities: BillingEntityOption[];
    logisticsIntegrations: LogisticsIntegrationOption[];
    invoicingIntegrations: InvoicingIntegrationOption[];
    orderStatuses: OrderStatusOption[];
  };

  /** Loading states, one per async operation. */
  loading: {
    savingInventory: boolean;
    savingBilling: boolean;
    savingLogistics: boolean;
    completing: boolean;
    provisioningInvoicing: boolean;
    creatingBilling: boolean;
    registeringLogistics: boolean;
  };

  /** Top-level errors. */
  errors: Record<string, string>;
}

export const initialState: SalesChannelSetupState = {
  data: {
    integrationId: null,
    accountId: null,
    connectorId: null,
    integrationUserId: null,
    channelSetupCompletedAt: null,
    currentSettings: {},
    currentBillingEntityId: null
  },
  ui: {
    manuallyOpened: false
  },
  publications: {
    // Default ON: someone connecting a sales channel almost always wants its
    // existing listings in Teselly, and the import is additive and re-runnable.
    autoImport: true,
    // Default ON, same as `autoImport` above — changed from OFF on the product
    // owner's call (TV1-2379). The earlier reasoning was that a full order-history
    // pull is heavy and one-way, so it should be opt-in; what makes opt-out the
    // better default is that the seeded orders are INERT. They are recorded as
    // history and fire nothing — no invoices, no stock movements, no buyer
    // messages, no channel writes (`channelOrderImport.policy.ts`, guarded by
    // `isBulkSyncWrite`). So the blast radius is "rows appear in Orders", and a
    // seller who connects a channel and finds no sales history is far more
    // surprised than one who finds it.
    autoImportOrders: true
  },
  inventory: {
    warehouseId: null,
    trigger: 'OrderPlaced',
    statusId: null,
    ruleId: null,
    channelStockRuleId: null
  },
  billing: {
    mode: 'select',
    billingEntityId: null,
    form: emptyBillingEntityFormData,
    formErrors: {},
    trigger: 'OrderPlaced',
    statusId: null,
    ruleId: null
  },
  invoicing: {
    selectedConnectorId: null,
    credentials: {},
    provisionedIntegrationId: null
  },
  logistics: {
    mode: 'select',
    logisticsIntegrationId: null,
    trigger: 'OrderPlaced',
    statusId: null,
    ruleId: null,
    create: {
      selectedConnectorId: null,
      credentials: {},
      instanceName: ''
    }
  },
  catalogs: {
    availableWarehouses: [],
    billingEntities: [],
    logisticsIntegrations: [],
    invoicingIntegrations: [],
    orderStatuses: []
  },
  loading: {
    savingInventory: false,
    savingBilling: false,
    savingLogistics: false,
    completing: false,
    provisioningInvoicing: false,
    creatingBilling: false,
    registeringLogistics: false
  },
  errors: {}
};

export const selectors = {
  /**
   * First-config detection: the wizard is required while a channel is loaded
   * and that channel has not yet recorded its setup completion marker.
   */
  selectIsSetupRequired: ( state: SalesChannelSetupState ): boolean => {
    return state.data.integrationId != null && !state.data.channelSetupCompletedAt;
  },

  /** Whether the user re-opened the wizard on demand after completion. */
  selectManuallyOpened: ( state: SalesChannelSetupState ): boolean =>
    state.ui.manuallyOpened,

  /**
   * Per-concern completion, for the inline setup checklist (TV1-2365).
   *
   * Each flag asks "is this already configured ON THE CHANNEL", not "does the
   * form currently hold a value" — the init policy pre-fills `ruleId` from the
   * channel's existing rules and `currentBillingEntityId` from the channel
   * itself, so these read the persisted truth. Keying off the *selection*
   * (`warehouseId`, `billing.billingEntityId`) would flip an item to done the
   * instant someone picked an option, before anything was saved.
   */
  selectSetupCompletion: ( state: SalesChannelSetupState ) => ({
    inventory: state.inventory.ruleId != null,
    billing: ( state.billing.billingEntityId ?? state.data.currentBillingEntityId ) != null,
    logistics: state.logistics.ruleId != null
  }),

  /** How many of the three concerns are configured. */
  selectSetupDoneCount: ( state: SalesChannelSetupState ): number => {
    const done = selectors.selectSetupCompletion( state );
    return Number( done.inventory ) + Number( done.billing ) + Number( done.logistics );
  },

  /**
   * The inventory step's rule can be saved once a warehouse is chosen and — when
   * the trigger is a status change — a status is chosen.
   */
  selectCanSaveInventory: ( state: SalesChannelSetupState ): boolean => {
    if ( !state.inventory.warehouseId ) return false;
    if ( state.inventory.trigger === 'OrderStatusChanged' && !state.inventory.statusId ) {
      return false;
    }
    return true;
  },

  /** The logistics rule can be saved once an integration + (status if needed) is chosen. */
  selectCanSaveLogistics: ( state: SalesChannelSetupState ): boolean => {
    if ( !state.logistics.logisticsIntegrationId ) return false;
    if ( state.logistics.trigger === 'OrderStatusChanged' && !state.logistics.statusId ) {
      return false;
    }
    return true;
  },

  /** Billing can finish once a billing entity is selected. */
  selectCanFinishBilling: ( state: SalesChannelSetupState ): boolean => {
    return ( state.billing.billingEntityId ?? '' ).trim().length > 0;
  },

  /**
   * The inline billing-create form is valid (mirrors the account-domain
   * `isBillingFormValid`): all required fields non-empty, including the
   * invoicing integration the entity issues through.
   */
  selectCanCreateBilling: ( state: SalesChannelSetupState ): boolean => {
    const { form } = state.billing;
    return (
      form.name.trim().length > 0 &&
      form.legalName.trim().length > 0 &&
      form.documentId.trim().length > 0 &&
      form.billingAddress.trim().length > 0 &&
      form.invoicingIntegrationId.trim().length > 0
    );
  }
};

/** Rule-definition ids the wizard creates, keyed by concern + trigger. */
export const WAREHOUSE_RULE_DEFINITION_IDS: Record<TriggerEvent, string> = {
  OrderPlaced: 'builtIn-fixed-warehouse',
  OrderStatusChanged: 'builtIn-fixed-warehouse-on-status'
};

export const LOGISTICS_RULE_DEFINITION_IDS: Record<TriggerEvent, string> = {
  OrderPlaced: 'builtIn-fixed-logistics-provider',
  OrderStatusChanged: 'builtIn-fixed-logistics-provider-on-status'
};

/**
 * The invoicing rule the billing step creates, keyed by trigger. OrderPlaced uses
 * a no-status sibling; OrderStatusChanged uses the status-gated definition.
 */
export const BILLING_RULE_DEFINITION_IDS: Record<TriggerEvent, string> = {
  OrderPlaced: 'builtIn-generate-invoice-on-placement',
  OrderStatusChanged: 'builtIn-generate-invoice-from-order'
};

export const BILLING_RULE_DEFINITION_ID_SET = new Set( Object.values( BILLING_RULE_DEFINITION_IDS ));

/** All warehouse/logistics rule-definition ids (for the re-run rule lookup). */
export const WAREHOUSE_RULE_DEFINITION_ID_SET = new Set( Object.values( WAREHOUSE_RULE_DEFINITION_IDS ));
export const LOGISTICS_RULE_DEFINITION_ID_SET = new Set( Object.values( LOGISTICS_RULE_DEFINITION_IDS ));

/**
 * The channel-stock rule the inventory step auto-creates alongside the fixed-warehouse
 * rule. It recomputes the channel listing stock from the same fixed warehouse and pushes
 * it to the channel on every warehouse-stock change. Unlike the warehouse rule it is NOT
 * order-triggered — it reacts to warehouse-stock events — so it has a single definition
 * regardless of the step's trigger.
 */
export const CHANNEL_STOCK_RULE_DEFINITION_ID = 'builtIn-channel-stock-formula-update';

/**
 * The `triggeringEvents` array the wizard PERSISTS onto the channel-stock rule row. It must stay
 * in step with the server's `channelStockFormulaUpdate.possibleTriggeringEvents`: a row is
 * filtered on this array by `findRulesToExecute`, so a name missing here is a rule that never
 * runs for every channel this wizard onboards.
 *
 * `WarehouseItemAvailableStockIncreased` was added by TV1-2114 / ADR-132 — without it a return's
 * restock never reached the marketplace, which went on advertising the pre-return number.
 * Existing rows written before that change are moved by the one-shot `SetRule` re-seed, never by
 * editing the readmodel.
 */
export const CHANNEL_STOCK_TRIGGERING_EVENTS = [
  'WarehouseItemAvailableStockDecreased',
  'WarehouseItemAvailableStockIncreased',
  'WarehouseItemAvailableStockSet'
];

/**
 * Build the channel-stock rule's `formula` input from the chosen fixed warehouse, so the
 * stock published to the channel comes from the same warehouse that fulfills its orders.
 */
export const buildChannelStockFormula = ( warehouseId: string ): string =>
  `{{(source:warehouses,warehouseId:'${warehouseId}')}}`;
